import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';

import { SDKSelector } from '../SDKSelector';
import { rootSideMenu } from '../../menus';
import { mergeSelectors, getSideMenu, getTitle } from './helper';
import { MenuLink } from './MenuLink';
import './SideNav.styles.scss';

interface SideNavProps {
  show: boolean;
  set: (b: boolean) => void;
  pathname: string;
  width?: number;
}

/**
 * The `SideMenu` renders persistent top-level links and a dropdown menu for selecting
 * the current docs SDK.
 */
export const SideNav = ({ show, set, pathname, width }: SideNavProps) => {
  const [mounted, setMounted] = useState(false);
  const menu = getSideMenu(pathname);
  const title = getTitle(pathname);
  const isMobile = width && width < 768;

  /**
   * Avoid cumulative layout shift on mobile devices for first render
   */
  useEffect(() => {
    if (show === false && !mounted) {
      setMounted(true);
    }
  }, [show, mounted]);

  return (
    <>
      {isMobile && mounted ? (
        <div
          className={mergeSelectors([
            'menu-overlay',
            show ? ' active' : undefined,
          ])}
          role="button"
          onClick={() => set(false)}
        />
      ) : null}
      <div
        id="sidenav"
        className={!show ? 'desktop-hide' : 'mobile-show'}
        style={
          isMobile && mounted
            ? { transition: 'transform 0.3s ease 0s' }
            : undefined
        }>
        <div className="container">
          <div className="side-menu">
            <ul className="top-nav">
              {rootSideMenu.map(({ label, path }, i) => (
                <li key={i}>
                  <Link to={path as string} activeClassName="active">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          {pathname.includes('/sdk_ref/') ? (
            <SDKSelector pathname={pathname} />
          ) : null}
          {title ? <div className="menu-title">{title}</div> : null}
          <ul className="collapse-menu" role="navigation">
            {menu.map((item, index) => (
              <MenuLink key={index} data={item} closeMenu={() => set(false)} />
            ))}
          </ul>
        </div>
      </div>
      <button
        id="collapse-sidenav"
        className={show ? 'rotate' : undefined}
        title="collapse side navigation"
        onClick={() => set(!show)}
        aria-label="collapse side navigation">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 19a1 1 0 01-.71-1.71l5.3-5.29-5.3-5.29a1 1 0 011.42-1.42l6 6a1 1 0 010 1.41l-6 6A1 1 0 019 19z"></path>
        </svg>
      </button>
    </>
  );
};
